// Asset Manager - Handles loading and managing game assets
export class AssetManager {
    constructor() {
        this.assets = {
            images: new Map(),
            audio: new Map(),
            json: new Map(),
            spritesheets: new Map()
        };
        
        this.loadingProgress = 0;
        this.loadedAssets = 0;
        this.totalAssets = 0;
        this.loadingCallbacks = [];
        
        this.basePath = '/assets/';
        this.maxConcurrentLoads = 6;
        this.currentLoads = 0;
        this.loadQueue = [];
        this.isLoading = false;
    }

    // Image loading
    async loadImage(key, src) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            
            img.onload = () => {
                this.assets.images.set(key, {
                    image: img,
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                    src: src
                });
                this.onAssetLoaded();
                resolve(img);
            };
            
            img.onerror = () => {
                reject(new Error(`Failed to load image: ${src}`));
            };
            
            img.src = this.basePath + src;
            this.totalAssets++;
            this.updateLoadingProgress();
        });
    }

    // Audio loading
    async loadAudio(key, src) {
        return new Promise((resolve, reject) => {
            const audio = new Audio();
            
            audio.addEventListener('canplaythrough', () => {
                this.assets.audio.set(key, {
                    audio: audio,
                    duration: audio.duration,
                    src: src
                });
                this.onAssetLoaded();
                resolve(audio);
            });
            
            audio.addEventListener('error', () => {
                reject(new Error(`Failed to load audio: ${src}`));
            });
            
            audio.src = this.basePath + src;
            audio.load();
            this.totalAssets++;
            this.updateLoadingProgress();
        });
    }

    // JSON loading
    async loadJSON(key, src) {
        try {
            const response = await fetch(this.basePath + src);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            this.assets.json.set(key, {
                data: data,
                src: src
            });
            
            this.onAssetLoaded();
            return data;
        } catch (error) {
            throw new Error(`Failed to load JSON: ${src} - ${error.message}`);
        }
    }

    // Spritesheet loading
    async loadSpritesheet(key, src, frameWidth, frameHeight, options = {}) {
        try {
            const image = await this.loadImage(key + '_image', src);
            
            const spritesheet = {
                image: image,
                frameWidth: frameWidth,
                frameHeight: frameHeight,
                frames: [],
                animations: new Map()
            };
            
            // Calculate frames
            const cols = Math.floor(image.naturalWidth / frameWidth);
            const rows = Math.floor(image.naturalHeight / frameHeight);
            
            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++) {
                    spritesheet.frames.push({
                        x: col * frameWidth,
                        y: row * frameHeight,
                        width: frameWidth,
                        height: frameHeight
                    });
                }
            }
            
            // Add animations if provided
            if (options.animations) {
                for (const [animName, animData] of Object.entries(options.animations)) {
                    spritesheet.animations.set(animName, {
                        frames: animData.frames,
                        frameRate: animData.frameRate || 10,
                        loop: animData.loop !== false
                    });
                }
            }
            
            this.assets.spritesheets.set(key, spritesheet);
            return spritesheet;
        } catch (error) {
            throw new Error(`Failed to load spritesheet: ${src} - ${error.message}`);
        }
    }

    // Batch loading
    async loadAssets(assetList) {
        console.log(`📦 Loading ${assetList.length} assets...`);
        
        const loadPromises = [];
        
        for (const asset of assetList) {
            const promise = this.loadAsset(asset);
            loadPromises.push(promise);
        }
        
        try {
            await Promise.all(loadPromises);
            console.log('✅ All assets loaded successfully!');
            return true;
        } catch (error) {
            console.error('❌ Asset loading failed:', error);
            throw error;
        }
    }

    async loadAsset(asset) {
        const { type, key, src, ...options } = asset;
        
        switch (type) {
            case 'image':
                return this.loadImage(key, src);
            case 'audio':
                return this.loadAudio(key, src);
            case 'json':
                return this.loadJSON(key, src);
            case 'spritesheet':
                return this.loadSpritesheet(key, src, options.frameWidth, options.frameHeight, options);
            default:
                throw new Error(`Unknown asset type: ${type}`);
        }
    }

    onAssetLoaded() {
        this.loadedAssets++;
        this.updateLoadingProgress();
        
        // Notify callbacks
        this.loadingCallbacks.forEach(callback => {
            callback(this.getLoadingProgress());
        });
    }

    updateLoadingProgress() {
        if (this.totalAssets > 0) {
            this.loadingProgress = this.loadedAssets / this.totalAssets;
        }
    }

    getLoadingProgress() {
        return this.loadingProgress;
    }

    isLoadingComplete() {
        return this.loadedAssets === this.totalAssets && this.totalAssets > 0;
    }

    addLoadingCallback(callback) {
        this.loadingCallbacks.push(callback);
    }

    removeLoadingCallback(callback) {
        const index = this.loadingCallbacks.indexOf(callback);
        if (index > -1) {
            this.loadingCallbacks.splice(index, 1);
        }
    }

    // Asset retrieval
    getImage(key) {
        const asset = this.assets.images.get(key);
        return asset ? asset.image : null;
    }

    getAudio(key) {
        const asset = this.assets.audio.get(key);
        return asset ? asset.audio : null;
    }

    getJSON(key) {
        const asset = this.assets.json.get(key);
        return asset ? asset.data : null;
    }

    getSpritesheet(key) {
        return this.assets.spritesheets.get(key);
    }

    getFrame(spritesheetKey, frameIndex) {
        const spritesheet = this.assets.spritesheets.get(spritesheetKey);
        if (!spritesheet || frameIndex < 0 || frameIndex >= spritesheet.frames.length) {
            return null;
        }
        return spritesheet.frames[frameIndex];
    }

    getAnimation(spritesheetKey, animationName) {
        const spritesheet = this.assets.spritesheets.get(spritesheetKey);
        if (!spritesheet) return null;
        return spritesheet.animations.get(animationName);
    }

    // Asset information
    getAssetInfo(key) {
        for (const assetType of Object.values(this.assets)) {
            if (assetType.has(key)) {
                return assetType.get(key);
            }
        }
        return null;
    }

    getLoadedAssets() {
        return {
            images: Array.from(this.assets.images.keys()),
            audio: Array.from(this.assets.audio.keys()),
            json: Array.from(this.assets.json.keys()),
            spritesheets: Array.from(this.assets.spritesheets.keys())
        };
    }

    getTotalAssets() {
        return this.totalAssets;
    }

    getLoadedCount() {
        return this.loadedAssets;
    }

    // Asset removal
    removeAsset(key) {
        for (const assetType of Object.values(this.assets)) {
            if (assetType.has(key)) {
                assetType.delete(key);
                this.loadedAssets--;
                this.updateLoadingProgress();
                return true;
            }
        }
        return false;
    }

    clearAssets() {
        for (const assetType of Object.values(this.assets)) {
            assetType.clear();
        }
        this.loadedAssets = 0;
        this.totalAssets = 0;
        this.loadingProgress = 0;
        this.loadingCallbacks = [];
    }

    // Error handling
    handleLoadingError(error, asset) {
        console.error(`Asset loading error for ${asset.key}:`, error);
        
        // Still count as loaded to prevent infinite loading
        this.onAssetLoaded();
        
        // Create placeholder for failed assets
        if (asset.type === 'image') {
            this.createPlaceholderImage(asset.key);
        } else if (asset.type === 'audio') {
            this.createPlaceholderAudio(asset.key);
        }
    }

    createPlaceholderImage(key) {
        const canvas = document.createElement('canvas');
        canvas.width = 64;
        canvas.height = 64;
        
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ff0000';
        ctx.fillRect(0, 0, 64, 64);
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('ERROR', 32, 32);
        
        this.assets.images.set(key, {
            image: canvas,
            width: 64,
            height: 64,
            src: 'placeholder',
            isPlaceholder: true
        });
    }

    createPlaceholderAudio(key) {
        // Create silent audio
        const audio = new Audio();
        audio.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABgAZGF0YQAAAAA=';
        
        this.assets.audio.set(key, {
            audio: audio,
            duration: 0,
            src: 'placeholder',
            isPlaceholder: true
        });
    }
}